import type { LicenseDetails } from '../../common/license-context.js';
import type { WorkfrontWorkspaceOption } from './types.js';

/**
 * Derives the workspace dropdown options from a license-details
 * payload. Pure function — extracted from
 * `WorkfrontCustomFormsMigrationPage` so it's testable without
 * React context.
 *
 *   - Primary always lives at `value='primary'`, label preferring URL
 *     over name (operators recognise URLs faster).
 *   - Secondaries use their server-minted `ref` (`secondary:<index>`)
 *     so the engine's `resolveWorkspace` indexes line up across the
 *     SPA → app-api → engine round-trip.
 *   - Returns an empty array when there's no license payload yet.
 *
 * The error-state messaging in `useWorkspaceError` (below) keys off
 * the result length: 0 → "no workspace"; 1 → "need a second";
 * ≥2 → wizard usable.
 */
export function buildWorkspaceOptions(
  details: LicenseDetails | null,
): ReadonlyArray<WorkfrontWorkspaceOption> {
  if (!details) return [];
  const options: WorkfrontWorkspaceOption[] = [];
  if (details.primaryWorkfrontConfig) {
    const p = details.primaryWorkfrontConfig;
    options.push({
      value: 'primary',
      text: p.url || p.name || 'Primary Workfront workspace',
    });
  }
  for (const sec of details.secondaryWorkfrontConfigs) {
    options.push({ value: sec.ref, text: sec.url || sec.name });
  }
  return options;
}

/**
 * Maps a workspace-options array to the warning string the wizard +
 * landing page surface to the operator. Returns `null` when the
 * wizard is unblocked (≥2 workspaces).
 */
export function buildWorkspaceError(
  options: ReadonlyArray<WorkfrontWorkspaceOption>,
): string | null {
  if (options.length === 0) {
    return 'No Workfront workspace is configured on this license. Ask your operator to wire a Workfront connection in the admin console.';
  }
  if (options.length < 2) {
    return 'Only one workspace is configured on this license. Custom-forms migration requires a destination workspace different from the source — add a secondary Workfront connection in the admin console.';
  }
  return null;
}
